// =============================================================================
// API Service
// =============================================================================
// Axios instance with interceptors for authentication
// =============================================================================

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../hooks/useAuth';

// =============================================================================
// Create Axios Instance
// =============================================================================

// Use environment variable for API URL, fallback to /api for local dev with proxy
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// =============================================================================
// Request Interceptor
// =============================================================================

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // For FormData, remove Content-Type so browser sets it with boundary
    if (config.data instanceof FormData && config.headers) {
      config.headers.delete('Content-Type');
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// =============================================================================
// Response Interceptor
// =============================================================================

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Refresh token is sent via HttpOnly cookie (withCredentials: true).
        // Also send from store as body fallback for backward compatibility with
        // sessions that were created before the cookie fix was deployed.
        const refreshToken = useAuthStore.getState().refreshToken;

        const response = await axios.post(`${API_BASE_URL}/auth/refresh`,
          refreshToken ? { refreshToken } : {},
          { withCredentials: true },
        );

        const { accessToken } = response.data;

        if (accessToken) {
          useAuthStore.getState().setAccessToken(accessToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }

          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - DON'T clear auth immediately
        // This prevents logout-on-login bug where a stale request triggers refresh
        // The ProtectedRoute component will handle redirect if truly unauthorized
        // Only reject the request, let the component handle auth state
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// =============================================================================
// API Error Handler
// =============================================================================

export interface ApiError {
  message: string;
  details?: Array<{ field: string; message: string }>;
}

export function getApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ error?: string; details?: Array<{ field: string; message: string }> }>;

    return {
      message: axiosError.response?.data?.error || axiosError.message || 'An error occurred',
      details: axiosError.response?.data?.details,
    };
  }

  if (error instanceof Error) {
    return { message: error.message };
  }

  return { message: 'An unexpected error occurred' };
}

// =============================================================================
// Phone Number Formatting
// =============================================================================

/**
 * Format phone number for display with dashes (e.g., 123-456-7890)
 */
export function formatPhoneDisplay(phone: string | null | undefined): string {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Format as XXX-XXX-XXXX for 10 digits
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Format as X-XXX-XXX-XXXX for 11 digits (with country code)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `${digits.slice(0, 1)}-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // Return original if not standard format
  return phone;
}

/**
 * Format phone number as user types (add dashes automatically)
 */
export function formatPhoneInput(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Limit to 10 digits
  const limited = digits.slice(0, 10);

  // Format with dashes
  if (limited.length <= 3) {
    return limited;
  }
  if (limited.length <= 6) {
    return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  }
  return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
}

/**
 * Strip phone formatting to get just digits
 */
export function stripPhoneFormat(phone: string): string {
  return phone.replace(/\D/g, '');
}
